import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { checkCircle } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { formatDateToMMDDYYYY } from "~/shared/utils/string-fns.ts";

import styles from "./index.module.css";
import { printHtmlInNewTab } from "./utils/print-html-in-new-tab.ts";

export type ConfirmationData = {
	confirmationNumber?: string;
	paymentAmount?: number;
	paymentAccount?: string;
	dueDate?: string | null;
	notes?: string;
	paidOnInvoices?: string[];
	// Actual number of invoices paid; undefined for lump-sum payments, which
	// are not tied to specific invoices. Do not derive this from
	// paidOnInvoices.length, since that array omits invoices with a null PO.
	paidInvoiceCount?: number;
	isLumpSum?: boolean;
};

type PaymentConfirmationModalProps = {
	confirmationData?: ConfirmationData;
	onClose?: () => void;
};

export const PaymentConfirmationModal = ({
	confirmationData,
	onClose,
}: PaymentConfirmationModalProps) => {
	const isMobile = useIsMobile();

	const confirmationDocument = useMemo(
		() => (
			<div>
				<h3>
					<i>Payment Confirmation</i>
				</h3>
				<p>
					<i>Thank you for your payment to Total Quality Logistics (TQL).</i>
				</p>
				<table style={{ borderCollapse: "collapse", width: "100%" }}>
					<thead>
						<tr>
							<th
								style={{
									width: "220px",
									textAlign: "left",
									paddingRight: "2rem",
									paddingBottom: "1rem",
									verticalAlign: "top",
								}}
							>
								Confirmation Number:
							</th>
							<th
								style={{
									textAlign: "left",
									paddingBottom: "1rem",
									verticalAlign: "top",
								}}
							>
								{confirmationData?.confirmationNumber ?? "N/A"}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td
								style={{
									width: "220px",
									paddingRight: "2rem",
									verticalAlign: "top",
								}}
							>
								Payment Amount (USD):
							</td>
							<td style={{ verticalAlign: "top" }}>
								{confirmationData?.paymentAmount?.toFixed(2) ?? "N/A"}
							</td>
						</tr>
						<tr>
							<td
								style={{
									width: "220px",
									paddingRight: "2rem",
									verticalAlign: "top",
								}}
							>
								Account:
							</td>
							<td style={{ verticalAlign: "top" }}>
								{confirmationData?.paymentAccount ?? "N/A"}
							</td>
						</tr>
						<tr>
							<td
								style={{
									width: "220px",
									paddingRight: "2rem",
									verticalAlign: "top",
								}}
							>
								Payment Due:
							</td>
							<td style={{ verticalAlign: "top" }}>
								{confirmationData?.dueDate
									? formatDateToMMDDYYYY(confirmationData.dueDate)
									: "N/A"}
							</td>
						</tr>
						<tr>
							<td
								style={{
									width: "220px",
									paddingRight: "2rem",
									verticalAlign: "top",
								}}
							>
								List of POs Paid:
							</td>
							<td style={{ verticalAlign: "top" }}>
								{confirmationData?.paidOnInvoices?.length
									? confirmationData.paidOnInvoices.join(", ")
									: "N/A"}
							</td>
						</tr>
						<tr>
							<td
								style={{
									width: "220px",
									paddingRight: "2rem",
									verticalAlign: "top",
								}}
							>
								Processing Time:
							</td>
							<td style={{ verticalAlign: "top" }}>3-5 business days</td>
						</tr>
					</tbody>
				</table>
				<div>
					<p>Notes:</p>
					<p>{confirmationData?.notes ?? "N/A"}</p>
					<hr />
					<p>
						<strong>
							800.580.3101 | <span style={{ color: "blue" }}>TQL.COM</span>
						</strong>
					</p>
				</div>
			</div>
		),
		[confirmationData],
	);

	const handlePrintConfirmation = useCallback(() => {
		printHtmlInNewTab(
			renderToStaticMarkup(confirmationDocument),
			"body{font-family:Arial,sans-serif;margin:2rem;}@media print{body{-webkit-print-color-adjust:exact;}}",
		);
	}, [confirmationDocument]);

	if (isMobile) {
		const invoiceCount = confirmationData?.paidInvoiceCount ?? 0;
		let message: string;
		if (confirmationData?.isLumpSum) {
			message = "Payment has been successfully processed!";
		} else if (invoiceCount > 1) {
			message = "Invoices have been successfully paid!";
		} else {
			message = "Invoice has been successfully paid!";
		}

		return (
			<TraxModal onClose={onClose ?? (() => undefined)} width={"90%"}>
				<div className={styles.mobileSuccessContent}>
					<SvgIcon className={styles.mobileCheckIcon} icon={checkCircle} />
					<p className={styles.mobileSuccessMessage}>{message}</p>
				</div>
			</TraxModal>
		);
	}

	return (
		<TraxModal
			onClose={onClose ?? (() => undefined)}
			title={"Payment Confirmation"}
			width={"33%"}
		>
			<h2>
				Confirmation Number: {confirmationData?.confirmationNumber ?? "N/A"}
			</h2>
			<div className={styles.successRow}>
				<div className={styles.successIcon}>
					<SvgIcon className={styles.checkIcon} icon={checkCircle} />
				</div>
				<p>
					Thank you. Your Payment of $
					{confirmationData?.paymentAmount?.toFixed(2) ?? "N/A"} has been
					successfully processed from{" "}
					{confirmationData?.paymentAccount ?? "N/A"}. You will receive an email
					regarding the transaction shortly. If you have any questions or
					concerns, please email{" "}
					<span className={styles.emailLink}>trax@tql.com</span>.
				</p>
			</div>
			<DialogActionsBar>
				<Button onClick={handlePrintConfirmation}>Print Confirmation</Button>
				<Button onClick={onClose}>Close</Button>
			</DialogActionsBar>
		</TraxModal>
	);
};
